import { getIntegrationData } from '@dashboard/modules/integration/services/integrationDataUtilities'
import { customFieldKey } from '@dashboard/modules/integration/services/integrationUtilities'
import {
  autoSyncLogicDefinitionForSegment,
  isAQuestion,
  renderText,
  segmentGroupSyncSetupCompletion,
  segmentName
} from '@dashboard/modules/segment/services/segmentUtilities'
import { stripHtml } from '@dashboard/services/string'
import type {
  ConditionOperator,
  ConditionSourceType,
  ConditionValueType
} from '@dashboard/components/common/liquidConditionals'
import type { Dimension } from '@dashboard/types/config'
import type { PartnerDataItem } from '@dashboard/modules/integration/data/partnerEndpoints'

export type EmailPersonalizationField = {
  id: string
  sourceType: ConditionSourceType
  label: string
  token: string
  defaultOperator: ConditionOperator
  operators: ConditionOperator[]
  valueType: ConditionValueType
  values?: { label: string; value: string }[]
}

type CatalogOptions = {
  integratedPartner: any
  segmentGroups: any[]
  questionForSegmentGroupId?: (segmentGroupId: string) => any
  onError?: () => void
  previousFields?: EmailPersonalizationField[]
}

const DEFAULT_OPERATORS: ConditionOperator[] = ['==', '!=', 'contains', '>', '<', '>=', '<=']
const TEXT_OPERATORS: ConditionOperator[] = ['==', '!=', 'contains']
const BOOLEAN_OPERATORS: ConditionOperator[] = ['==', '!=']
const TAG_OPERATORS: ConditionOperator[] = ['contains']

export function isMappedEmailPersonalizationQuestion(segmentGroup: Dimension): boolean {
  if (!isAQuestion(segmentGroup) || !segmentGroup.syncToCustomField) {
    return false
  }

  const completion = segmentGroupSyncSetupCompletion(segmentGroup)
  return completion === 'some_values' || completion === 'all_values'
}

export async function buildEmailPersonalizationFieldCatalog({
  integratedPartner,
  segmentGroups,
  questionForSegmentGroupId,
  onError,
  previousFields = []
}: CatalogOptions): Promise<EmailPersonalizationField[]> {
  if (!integratedPartner) {
    return []
  }
  const fields: EmailPersonalizationField[] = []
  const liquidVariable = partnerLiquidVariable(integratedPartner)

  const sources: { type: ConditionSourceType; fields: Promise<EmailPersonalizationField[]> }[] = [
    {
      type: 'segment',
      fields: buildSegmentFields(segmentGroups, questionForSegmentGroupId, liquidVariable, integratedPartner)
    },
    { type: 'native_field', fields: buildNativeFields(liquidVariable) },
    { type: 'custom_field', fields: buildCustomFields(liquidVariable) },
    { type: 'tag', fields: buildTagFields(liquidVariable, integratedPartner) }
  ]
  const results = await Promise.allSettled(sources.map((source) => source.fields))
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      fields.push(...result.value)
    } else {
      onError?.()
      const source = sources[index]
      if (source === undefined) {
        throw new Error('Missing personalization field source')
      }
      fields.push(...previousFields.filter((field) => field.sourceType === source.type))
    }
  })

  const seen = new Set<string>()
  return fields.filter((field) => {
    const key = `${field.sourceType}:${field.token}:${field.id}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

function partnerLiquidVariable(integratedPartner: any): string | null {
  if (integratedPartner && Object.prototype.hasOwnProperty.call(integratedPartner, 'liquidVariable')) {
    return integratedPartner.liquidVariable || null
  }
  return 'subscriber'
}

function tokenFor(liquidVariable: string | null, fieldToken: string): string {
  if (!liquidVariable) {
    return fieldToken
  }
  if (fieldToken.includes('.')) {
    return fieldToken
  }
  return `${liquidVariable}.${fieldToken}`
}

function operatorForPartner(integratedPartner: any): ConditionOperator {
  return integratedPartner?.customFieldComparisonOperator || integratedPartner?.operator || '=='
}

function valueTypeForDataType(dataType?: string): ConditionValueType {
  const normalized = String(dataType || '').toLowerCase()
  if (['number', 'integer', 'float', 'decimal'].includes(normalized)) {
    return 'number'
  }
  if (['boolean', 'bool'].includes(normalized)) {
    return 'boolean'
  }
  return 'string'
}

function operatorsForDataType(dataType?: string): ConditionOperator[] {
  const valueType = valueTypeForDataType(dataType)
  if (valueType === 'number') {
    return DEFAULT_OPERATORS
  }
  if (valueType === 'boolean') {
    return BOOLEAN_OPERATORS
  }
  return TEXT_OPERATORS
}

async function buildSegmentFields(
  segmentGroups: any[],
  questionForSegmentGroupId: ((segmentGroupId: string) => any) | undefined,
  liquidVariable: string | null,
  integratedPartner: any
): Promise<EmailPersonalizationField[]> {
  return Promise.all(
    segmentGroups
      .filter((segmentGroup) => isMappedEmailPersonalizationQuestion(segmentGroup))
      .map(async (segmentGroup) => {
        const question = questionForSegmentGroupId?.(segmentGroup.id)
        const label = stripHtml(renderText(question?.text || segmentGroup.name || 'Question', 1000) || '')
        const rawField = String(segmentGroup.syncToCustomField)
        const fieldToken = await customFieldKey(rawField)
        const values = segmentGroup.segments
          .map((segment: any) => {
            const syncCondition = autoSyncLogicDefinitionForSegment(segment)
            const syncValue = syncCondition ? syncCondition.value : null
            if (
              !syncValue ||
              (typeof syncValue !== 'string' && typeof syncValue !== 'number' && typeof syncValue !== 'boolean')
            ) {
              return null
            }
            return {
              label: stripHtml(segmentName(segment)),
              value: String(syncValue)
            }
          })
          .filter(Boolean) as { label: string; value: string }[]

        return {
          id: `segment:${segmentGroup.id}`,
          sourceType: 'segment' as const,
          label,
          token: tokenFor(liquidVariable, fieldToken),
          defaultOperator: operatorForPartner(integratedPartner),
          operators: TEXT_OPERATORS,
          valueType: 'string' as const,
          values
        }
      })
  ).then((fields) => fields.filter((field) => field.token))
}

async function buildNativeFields(liquidVariable: string | null): Promise<EmailPersonalizationField[]> {
  const nativeFields = await getIntegrationData('partner', 'native_fields')
  return nativeFields.value
    .map((field: PartnerDataItem & { type?: string }) => {
      const fieldToken = String(field.templatingToken || field.key || field.id || field.name || '')
      const valueType = valueTypeForDataType(field.dataType || field.type)
      return {
        id: `native_field:${field.id || fieldToken}`,
        sourceType: 'native_field' as const,
        label: field.label || field.name || fieldToken,
        token: fieldToken ? tokenFor(liquidVariable, fieldToken) : '',
        defaultOperator: '==' as const,
        operators: operatorsForDataType(field.dataType || field.type),
        valueType
      }
    })
    .filter((field: EmailPersonalizationField) => field.token)
}

async function buildCustomFields(liquidVariable: string | null): Promise<EmailPersonalizationField[]> {
  const customFields = await getIntegrationData('partner', 'custom_fields')
  return await Promise.all(
    customFields.value.map(async (field: PartnerDataItem & { type?: string }) => {
      const fieldToken = await customFieldKey(String(field.id || field.key || field.templatingToken || ''))
      const valueType = valueTypeForDataType(field.dataType || field.type)
      return {
        id: `custom_field:${field.id || fieldToken}`,
        sourceType: 'custom_field' as const,
        label: field.label || field.name || fieldToken,
        token: fieldToken ? tokenFor(liquidVariable, fieldToken) : '',
        defaultOperator: '==' as const,
        operators: operatorsForDataType(field.dataType || field.type),
        valueType
      }
    })
  ).then((fields) => fields.filter((field: EmailPersonalizationField) => field.token))
}

async function buildTagFields(
  liquidVariable: string | null,
  integratedPartner: any
): Promise<EmailPersonalizationField[]> {
  if (!integratedPartner?.espFeatureSupport?.tag) {
    return []
  }

  const tags = await getIntegrationData('partner', 'tags')
  const values = tags.value.map((tag) => {
    const label = String(tag.label || tag.name || tag.id)
    return { label, value: String(label) }
  })

  if (!values.length) {
    return []
  }

  return [
    {
      id: 'tag:tags',
      sourceType: 'tag' as const,
      label: 'Tags',
      token: tokenFor(liquidVariable, 'tags'),
      defaultOperator: 'contains' as const,
      operators: TAG_OPERATORS,
      valueType: 'string' as const,
      values
    }
  ].filter((field: EmailPersonalizationField) => field.token)
}
